import { useData } from "@hooks/useData";
import { components } from "@apiSchema";
import http from "@core/fetch";
import apiConfig from "@apiConfig";

type Result = components['schemas']['SsbChainStrategyResult'];
type GenerateRequest = components['schemas']['GenerateSalesStrategyRequest'];
type GenerateResult = components['schemas']['SsbGenerateStrategyResult'];
export type SalesStrategySection = components['schemas']['SsbStrategySection'];
export type SalesStrategyState = NonNullable<Result['state']>;

export interface SalesStrategyData {
    state: SalesStrategyState;
    isPartial: boolean;
    sections: SalesStrategySection[];
    userInput: string | null;
    createdAt: string | null;
}

export const DEFAULT_SALES_STRATEGY_DATA: SalesStrategyData = {
    state: 'NotAvailable',
    isPartial: false,
    sections: [],
    userInput: null,
    createdAt: null,
};

export type AbortablePromise<T> = Promise<T> & { abort?: () => void };

function withAbort<T>(source: AbortablePromise<unknown>, mapped: Promise<T>): AbortablePromise<T> {
    const result = mapped as AbortablePromise<T>;
    result.abort = source.abort;
    return result;
}

const REQUEST_TYPE: keyof typeof apiConfig = 'get sales strategy';
const GENERATE_REQUEST_TYPE: keyof typeof apiConfig = 'generate sales strategy';
const GET_BY_ID_REQUEST_TYPE: keyof typeof apiConfig = 'get sales strategy by id';

function toSalesStrategyData(result: Result | null): SalesStrategyData {
    return {
        state: result?.state ?? 'NotAvailable',
        isPartial: result?.isPartial ?? false,
        sections: result?.strategy?.sections ?? [],
        userInput: result?.strategy?.userInput ?? result?.lastUserInput ?? null,
        createdAt: result?.strategy?.createdAt ?? null,
    };
}

export async function getSalesStrategy(chainId: string | null): Promise<SalesStrategyData> {
    if (chainId == null) return DEFAULT_SALES_STRATEGY_DATA;

    const result: Result = await http.get(REQUEST_TYPE, {
        chainId,
        includeMarkdown: false,
    });

    return toSalesStrategyData(result);
}

export function useSalesStrategy(chainId: string | null) {
    return useData(DEFAULT_SALES_STRATEGY_DATA, getSalesStrategy, chainId);
}

export function generateSalesStrategy(chainId: number, userInput: string | null): AbortablePromise<number> {
    const request: GenerateRequest = { chainId, userInput };
    const xhrPromise: AbortablePromise<GenerateResult> = http.postXHR(GENERATE_REQUEST_TYPE, {}, JSON.stringify(request));

    return withAbort(xhrPromise, xhrPromise.then((result) => {
        if (result.strategyId == null) {
            throw new Error('generateSalesStrategy: response missing strategyId');
        }

        return result.strategyId;
    }));
}

export function getSalesStrategyById(strategyId: number): AbortablePromise<SalesStrategyData> {
    const xhrPromise: AbortablePromise<Result> = http.getXHR(GET_BY_ID_REQUEST_TYPE, {
        strategyId,
        includeMarkdown: false,
    });

    return withAbort(xhrPromise, xhrPromise.then(toSalesStrategyData));
}
